import {useCallback, useMemo} from 'react';
import {addDoc, collection, deleteDoc, deleteField, doc, query, updateDoc, where,} from 'firebase/firestore';
import {useCollection} from 'react-firebase-hooks/firestore';
import {Lesson, LessonInsertOptions, LessonUpdateOptions} from '../types/Lesson';
import {firestore} from "../App";
import moment from "moment/moment";
import {MONGO_DATE_TIME_FORMAT} from "../utils/date";

interface UseLessonManagerProps {
    plannerId: string;
    timetableId: string;
}

export function useLessonManager({plannerId, timetableId}: UseLessonManagerProps) {

    const lessonsQuery = useMemo(() => {
        if (!plannerId || !timetableId) return null;

        return query(
            collection(firestore, 'syncedPlanners', plannerId, 'lessons'),
            where("timetableId", "==", timetableId)
        );
    }, [plannerId, timetableId]);

    const [lessonsSnapshot, loadingLessons, errorLessons] = useCollection(lessonsQuery);

    const lessons = useMemo(() => {
        return lessonsSnapshot?.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as Lesson)) || [];
    }, [lessonsSnapshot]);

    /**
     * Inserts a lesson document in the database.
     * Returns the ID of the new lesson.
     */
    const createLesson = useCallback(async (options: LessonInsertOptions): Promise<string | undefined> => {
        if (!plannerId || !timetableId) return;

        const lessonDoc: any = {
            plannerId: plannerId,
            timetableId: timetableId,
            subjectId: options.subjectId,
            title: options.title,
            color: options.color,
            room: options.room,
            note: options.note,
            createdOn: moment().format(MONGO_DATE_TIME_FORMAT)
        };

        // Firestore does not allow 'undefined' values.
        Object.keys(lessonDoc).forEach(key => {
            if (lessonDoc[key] === undefined) {
                delete lessonDoc[key];
            }
        });

        const colRef = collection(firestore, 'syncedPlanners', plannerId, 'lessons');

        try {
            console.log("Creating Lesson: ", lessonDoc);
            const docRef = await addDoc(colRef, lessonDoc);
            console.log("Lesson created successfully with ID: ", docRef.id);
            return docRef.id;
        } catch (error) {
            console.error("Error creating Lesson: ", error);
        }
    }, [plannerId, timetableId]);

    /**
     * Updates a lesson document in the database.
     */
    const updateLesson = useCallback(async (lessonId: string, options: LessonUpdateOptions) => {
        if (!plannerId || !timetableId) return;

        const docRef = doc(firestore, 'syncedPlanners', plannerId, 'lessons', lessonId);

        const updateData = {
            subjectId: options.subjectId ?? deleteField(),
            title: options.title ?? deleteField(),
            color: options.color ?? deleteField(),
            room: options.room ?? deleteField(),
            note: options.note ?? deleteField(),
        };

        await updateDoc(docRef, updateData);
    }, [plannerId, timetableId]);

    /**
     * Deletes a lesson from the database.
     */
    const deleteLesson = useCallback(async (lessonId: string) => {
        if (!plannerId || !timetableId) return;

        const docRef = doc(firestore, 'syncedPlanners', plannerId, 'lessons', lessonId);
        await deleteDoc(docRef);
    }, [plannerId, timetableId]);

    return {
        lessons,
        loadingLessons,
        errorLessons,
        createLesson,
        updateLesson,
        deleteLesson
    };
}